import "./styles.scss";

import { useRef, useState } from "react";
import CustomModal from "@components/CustomModal";
import { Input, Text, Icon } from "@fragments";
import { IconMailForward, IconUserPlus } from "@tabler/icons-react";

import api from "@helpers/api";

const INITIAL = { email: "", split_percentage: "", notes: "" };

/**
 * Send invitation — §6.5. Imperative open like ProgramEditorModal:
 *   modalRef.current.open(program, onSent)
 * Fields: e-mail (required) · optional split override · optional message. 422s
 * map to field errors + a toast (inactive program / duplicate enrollment).
 */
export default function InviteModal({ modalRef, communityId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});

  const programRef = useRef(null);
  const onSentRef = useRef(null);

  modalRef.current = {
    open(program, onSent) {
      programRef.current = program ?? null;
      onSentRef.current = onSent;
      setForm(INITIAL);
      setErrors({});
      setOpen(true);
    },
  };

  const program = programRef.current;
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function handleSubmit() {
    setLoading(true);
    setErrors({});

    const payload = {
      email: form.email,
      split_percentage:
        form.split_percentage === "" ? null : Number(form.split_percentage),
      notes: form.notes || null,
    };

    const res = await api.post(
      `community/${communityId}/mentoring/programs/${program?.id}/invitations`,
      payload,
    );

    setLoading(false);

    if (res?.response?.status >= 400) {
      setErrors(mapErrors(res.response.data?.errors));
      window.setSnackbar?.(res.response.data?.message || "Não foi possível enviar o convite");
      return;
    }

    window.setSnackbar?.("Convite enviado");
    onSentRef.current?.(res.data);
    setOpen(false);
  }

  const splitHelper =
    errors.split_percentage ||
    `Sobrescreve o repasse padrão${
      program?.split_percentage != null ? ` (${program.split_percentage}%)` : ""
    }`;

  return (
    <CustomModal
      maxWidth={520}
      open={open}
      handleClose={() => setOpen(false)}
      title="Convidar criador"
      bottomButtonSumbitText="Enviar convite"
      bottomButtonSumbitIcon={<IconMailForward size={18} />}
      handleSubmit={handleSubmit}
      loading={loading}
      bodyComponent={
        open ? (
          <div className="invite-modal">
            <div className="invite-modal-head">
              <Icon.Sphere size={56}>
                <IconUserPlus />
              </Icon.Sphere>
              <div className="invite-modal-head-text">
                <Text.h5 contrast={9} weight={500}>
                  Convide um criador para o programa
                </Text.h5>
                {program && (
                  <Text.span contrast={5} maxLines={1}>
                    {program.name}
                  </Text.span>
                )}
              </div>
            </div>

            <Input.Email
              label="E-mail do convidado"
              value={form.email}
              onInput={(v) => set("email", v)}
              error={!!errors.email}
              helper={errors.email}
              autoFocus
            />
            <Input.Integer
              label="Repasse personalizado (opcional)"
              value={form.split_percentage}
              onInput={(v) => set("split_percentage", v)}
              max={3}
              end="%"
              error={!!errors.split_percentage}
              helper={splitHelper}
              placeholder="Padrão do programa"
            />
            <Input.Text
              label="Mensagem (opcional)"
              value={form.notes}
              onInput={(v) => set("notes", v)}
              multiline
              placeholder="Inclua um recado pessoal no convite"
            />
          </div>
        ) : null
      }
    />
  );
}

function mapErrors(fieldErrors = {}) {
  return Object.entries(fieldErrors).reduce((acc, [key, messages]) => {
    acc[key] = Array.isArray(messages) ? messages[0] : String(messages);
    return acc;
  }, {});
}
